// =============================================================================
// VandieZ agent-web -- Marketplace (/marketplace)
// =============================================================================
// Lists open loads from the marketplace that are available for bidding.
// Agents browse on behalf of the currently-selected owner and click through
// to LoadBidPage to place a bid.
//
// Phase 2 TODO: add lane + vehicle-type filters at the top.
// =============================================================================

import { ChevronRight, Clock, Loader2, Package } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { loadsApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { ApiError } from "@/types/auth";
import type { Load, LoadStatus } from "@/types/load";

// Both maps are keyed on the REAL seven statuses. They previously
// carried four the backend never writes (`bid_window_closing`,
// `in_negotiation`, `in_transit`, `delivered`) and omitted two it does
// (`draft`, `postponed`) -- so a load in either real state rendered
// `undefined` as its label with no tone class at all. Being exhaustive
// `Record<LoadStatus, ...>` is what makes the compiler catch the next
// drift instead of the UI absorbing it.
const STATUS_LABEL: Record<LoadStatus, string> = {
  draft: "Draft",
  open_for_bidding: "Open for bidding",
  accepted: "Accepted",
  completed: "Completed",
  cancelled: "Cancelled",
  postponed: "Postponed",
  expired: "Expired",
};

const STATUS_TONE: Record<LoadStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  open_for_bidding: "bg-success-subtle text-success-subtle-foreground",
  accepted: "bg-primary-subtle text-primary-subtle-foreground",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-danger-subtle text-danger-subtle-foreground",
  postponed: "bg-warning-subtle text-warning-subtle-foreground",
  expired: "bg-muted text-muted-foreground",
};

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function bidWindowRemaining(endsAt: string): string {
  const now = Date.now();
  const end = new Date(endsAt).getTime();
  const diffMs = end - now;
  if (diffMs <= 0) {return "Closed";}
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) {return `${diffMin}m left`;}
  const hrs = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return mins > 0 ? `${hrs}h ${mins}m left` : `${hrs}h left`;
}

export function MarketplacePage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const { currentOwnerId } = useOwnerContext();
  const [loads, setLoads] = useState<Load[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (accessToken === null || currentOwnerId === null) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    loadsApi
      .list(
        accessToken,
        currentOwnerId,
        { status: "open_for_bidding", limit: 50 },
        controller.signal,
      )
      .then((res) => {
        // Sort by created_at DESC
        const sorted = [...res.items].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        setLoads(sorted);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        if (err instanceof ApiError) {
          const code = err.problem.code;
          if (err.problem.status === 403 && code === "AGENT_PERMISSION_MISSING") {
            setError("Ask the owner to grant the 'loads:read' permission on 'loads'.");
          } else if (err.problem.status === 403 && code === "AGENT_NO_GRANT_FOR_OWNER") {
            setError("Your access to this owner was revoked. Switch owner via the header dropdown.");
          } else if (err.problem.status === 422 && code === "AGENT_OWNER_PARAM_REQUIRED") {
            setError("No owner selected — switch via the header dropdown.");
          } else {
            setError(err.problem.detail);
          }
        } else {
          setError("Couldn't load marketplace loads.");
        }
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, currentOwnerId]);

  if (currentOwnerId === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Marketplace" />
        <main className="mx-auto max-w-4xl px-6 py-16 text-center text-muted-foreground">
          No owner selected — head to{" "}
          <Link to="/" className="font-medium text-primary underline underline-offset-2">
            My Owners
          </Link>{" "}
          and pick one.
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <AppHeader title="Marketplace" />

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        {/* Phase 2 TODO: add lane + vehicle-type filters here */}

        {error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : loads === null || loads.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Package className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No open loads right now</CardTitle>
              <CardDescription>
                No open loads matching your area right now. Check back in a few
                minutes — shippers post throughout the day.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-3">
            {loads.map((load) => (
              <Link
                key={load.id}
                to={`/marketplace/${load.id}?as=${currentOwnerId}`}
                className="block transition-colors hover:opacity-95"
              >
                <Card className="group cursor-pointer transition-colors hover:border-primary/50">
                  <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[load.status]}`}
                        >
                          {STATUS_LABEL[load.status]}
                        </span>
                        <span className="text-sm font-medium">
                          {load.pickup_district} → {load.drop_district}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {load.material_category} · {load.weight_kg} kg ·{" "}
                        {load.required_vehicle_type}
                      </p>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {load.budget_visible_to_bidders ? (
                          <span>
                            {formatINR(load.budget_min)} – {formatINR(load.budget_max)}
                          </span>
                        ) : (
                          <span className="italic">Budget hidden</span>
                        )}
                        <span>{load.estimated_distance_km} km</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {bidWindowRemaining(load.bid_window_ends_at)}
                      <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
